import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarBlankIcon as CalendarBlank, PlusIcon as Plus, TrashIcon as Trash, TagIcon as Tag, ListChecksIcon as ListChecks, MapPinIcon as MapPin, CreditCardIcon as CreditCard, HashIcon as Hash, FloppyDiskIcon as FloppyDisk, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown } from "@phosphor-icons/react";

const clone = (o) => JSON.parse(JSON.stringify(o));

// Lista modificabile generica (label + attivo, opzionale color/default)
function EditableList({ items, onChange, withColor, withDefault, addPlaceholder, testid }) {
  const upd = (i, k, v) => { const n = clone(items); n[i][k] = v; if (withDefault && k === "default" && v) n.forEach((x, j) => { if (j !== i) x.default = false; }); onChange(n); };
  const add = () => onChange([...items, withColor ? { key: "", label: "", color: "#2563eb", attivo: true, default: false } : { key: "", label: "", attivo: true }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const n = clone(items); [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
          <div className="flex flex-col shrink-0" title="Sposta">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testid}-up-${i}`}><CaretUp size={12} weight="bold" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testid}-down-${i}`}><CaretDown size={12} weight="bold" /></button>
          </div>
          {withColor && (
            <input type="color" value={it.color || "#2563eb"} onChange={(e) => upd(i, "color", e.target.value)}
              className="h-9 w-10 rounded-sm border border-border cursor-pointer shrink-0" data-testid={`${testid}-color-${i}`} title="Colore" />
          )}
          <Input value={it.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder={addPlaceholder} className="rounded-sm" data-testid={`${testid}-label-${i}`} />
          {withDefault && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 px-2" title="Valore di default per i nuovi interventi">
              <input type="radio" name={`${testid}-default`} checked={!!it.default} onChange={() => upd(i, "default", true)} data-testid={`${testid}-default-${i}`} /> default
            </label>
          )}
          <div className="flex items-center gap-1.5 shrink-0" title="Attivo">
            <Switch checked={it.attivo !== false} onCheckedChange={(v) => upd(i, "attivo", v)} data-testid={`${testid}-active-${i}`} />
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}><Trash size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-1" onClick={add} data-testid={`${testid}-add`}><Plus size={14} /> Aggiungi</Button>
    </div>
  );
}

const Block = ({ icon: Icon, title, desc, children }) => (
  <div className="border-t border-border pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-primary" />
      <div>
        <h4 className="font-heading font-semibold text-sm">{title}</h4>
        {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

export function InterventiConfigSection() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/interventi-config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const setPart = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setCal = (k, v) => setCfg((c) => ({ ...c, calendario: { ...c.calendario, [k]: v } }));
  const setNum = (k, v) => setCfg((c) => ({ ...c, numerazione: { ...(c.numerazione || {}), [k]: v } }));

  const save = async () => {
    try {
      const { data } = await api.put("/interventi-config", cfg);
      setCfg(data);
      toast.success("Configurazione Attività/Interventi salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cfg) return null;
  const cal = cfg.calendario || {};
  const num = cfg.numerazione || {};
  const previewNum = (() => {
    const n = Math.max(1, parseInt(num.prossimo_numero) || 1);
    const pad = Math.max(0, parseInt(num.padding) || 0);
    const numstr = pad > 0 ? String(n).padStart(pad, "0") : String(n);
    return num.prefisso ? `${num.prefisso}-${numstr}` : numstr;
  })();

  return (
    <Card className="rounded-sm border-border shadow-none p-6 mb-6" data-testid="interventi-config-section">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><ListChecks size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Attività / Interventi</h3>
            <p className="text-xs text-muted-foreground">Stati, modalità di addebito, zone e impostazioni calendario del modulo Interventi</p>
          </div>
        </div>
        <Button onClick={save} className="rounded-sm shrink-0" data-testid="save-interventi-config-top-btn">Salva configurazione</Button>
      </div>

      <Block icon={Tag} title="Stati intervento" desc="Colore, attivazione e stato di default per i nuovi interventi">
        <EditableList items={cfg.stati || []} onChange={(v) => setPart("stati", v)} withColor withDefault addPlaceholder="Nome stato" testid="cfg-stati" />
      </Block>

      <Block icon={CreditCard} title="Modalità addebito" desc="Categoria operativa dell'intervento (usata per filtri calendario e logica di addebito). La modalità impostata come default viene preselezionata sui nuovi interventi.">
        <EditableList items={cfg.modalita || []} onChange={(v) => setPart("modalita", v)} withDefault addPlaceholder="Nome modalità" testid="cfg-modalita" />
      </Block>

      <Block icon={MapPin} title="Zone" desc="Aree geografiche per filtrare e organizzare gli interventi">
        <EditableList items={cfg.zone || []} onChange={(v) => setPart("zone", v)} addPlaceholder="Nome zona" testid="cfg-zone" />
      </Block>

      <Block icon={CalendarBlank} title="Impostazioni calendario">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Vista predefinita</Label>
            <Select value={cal.vista_default} onValueChange={(v) => setCal("vista_default", v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="cfg-cal-vista"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mese</SelectItem>
                <SelectItem value="week">Settimana</SelectItem>
                <SelectItem value="day">Giorno</SelectItem>
                <SelectItem value="agenda">Agenda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Durata slot</Label>
            <Select value={String(cal.slot_minuti)} onValueChange={(v) => setCal("slot_minuti", Number(v))}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="cfg-cal-slot"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minuti</SelectItem>
                <SelectItem value="30">30 minuti</SelectItem>
                <SelectItem value="60">60 minuti</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Criterio colore</Label>
            <Select value={cal.colore_criterio} onValueChange={(v) => setCal("colore_criterio", v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="cfg-cal-colore"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stato">Per stato</SelectItem>
                <SelectItem value="tipo">Per tipo (futuro)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Orario visibile da</Label>
            <Input type="time" value={cal.ora_inizio || "08:00"} onChange={(e) => setCal("ora_inizio", e.target.value)} className="mt-1 rounded-sm" data-testid="cfg-cal-inizio" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Orario visibile a</Label>
            <Input type="time" value={cal.ora_fine || "18:00"} onChange={(e) => setCal("ora_fine", e.target.value)} className="mt-1 rounded-sm" data-testid="cfg-cal-fine" />
          </div>
        </div>
      </Block>

      <Block icon={Hash} title="Numerazione rapportini" desc="Numero progressivo assegnato automaticamente al salvataggio. In produzione parte da 1.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Prossimo numero</Label>
            <Input type="number" min="1" value={num.prossimo_numero ?? 1} onChange={(e) => setNum("prossimo_numero", e.target.value === "" ? 1 : Number(e.target.value))} className="mt-1 rounded-sm" data-testid="cfg-num-prossimo" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Prefisso (opzionale)</Label>
            <Input value={num.prefisso || ""} onChange={(e) => setNum("prefisso", e.target.value)} placeholder="es. RAPP" className="mt-1 rounded-sm" data-testid="cfg-num-prefisso" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Padding cifre</Label>
            <Input type="number" min="0" max="8" value={num.padding ?? 0} onChange={(e) => setNum("padding", e.target.value === "" ? 0 : Number(e.target.value))} className="mt-1 rounded-sm" data-testid="cfg-num-padding" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Anteprima</Label>
            <div className="mt-1 h-9 flex items-center px-3 rounded-sm border border-border bg-secondary/40 font-mono text-sm" data-testid="cfg-num-preview">{previewNum}</div>
          </div>
        </div>
        <label className="flex items-center gap-2.5 mt-3 text-sm cursor-pointer">
          <Switch checked={num.blocco_modifica !== false} onCheckedChange={(v) => setNum("blocco_modifica", v)} data-testid="cfg-num-blocco" />
          <span>Blocca la modifica manuale del numero nella scheda intervento</span>
        </label>
      </Block>

      <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-border flex justify-end z-10">
        <Button onClick={save} className="rounded-sm gap-2 shadow-sm" data-testid="save-interventi-config-btn"><FloppyDisk size={16} weight="bold" /> Salva configurazione</Button>
      </div>
    </Card>
  );
}
